import type React from 'react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { ArrowLeft, Sparkles, AlertCircle } from 'lucide-react';
import { broadcastBriefsChanged } from '../lib/react-query';
import {
  ResearchProgressTracker,
  type ResearchStage,
  type StageStatus
} from './ResearchProgressTracker';
import {
  Select,
  SelectContent,
  SelectItemWithDescription,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  CONTENT_TYPES,
  CONTENT_LENGTHS,
  SOURCE_AGE_OPTIONS,
  type ContentTypeValue,
  type ContentLengthValue,
  type SourceAgeValue
} from '../constants/deepResearch';

interface DeepResearchFlowProps {
  onBack: () => void;
}

interface ResearchProgressEvent {
  type: 'stage_update' | 'complete' | 'error';
  stage?: ResearchStage;
  status?: StageStatus;
  message?: string;
  error?: string;
  data?: {
    id: string;
    title: string;
    task: string;
    description: string;
    urls: string[];
  };
}

export const DeepResearchFlow: React.FC<DeepResearchFlowProps> = ({
  onBack
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<'input' | 'progress'>('input');
  const [topic, setTopic] = useState('');
  const [description, setDescription] = useState('');
  const [contentType, setContentType] = useState<ContentTypeValue>('linkedin-article');
  const [contentLength, setContentLength] = useState<ContentLengthValue>('medium');
  const [sourceAge, setSourceAge] = useState<SourceAgeValue>('any');
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill form from URL parameters (from Content Inspiration)
  useEffect(() => {
    const titleParam = searchParams.get('title');
    const taskParam = searchParams.get('task');

    if (titleParam) {
      setTopic(titleParam);
    }
    if (taskParam) {
      setDescription(taskParam);
    }
    // Note: The collect-sources-only endpoint generates its own URLs via search,
    // so we don't need to pass URLs from Content Inspiration
  }, [searchParams]);

  // Progress tracking
  const [currentStage, setCurrentStage] = useState<ResearchStage>('query_generation');
  const [stages, setStages] = useState<Record<ResearchStage, StageStatus>>({
    query_generation: 'pending',
    source_collection: 'pending',
    url_validation: 'pending',
    content_processing: 'pending',
    indexing: 'pending',
    planning: 'pending',
    writing: 'pending'
  });
  const [messages, setMessages] = useState<Record<ResearchStage, string>>({
    query_generation: '',
    source_collection: '',
    url_validation: '',
    content_processing: '',
    indexing: '',
    planning: '',
    writing: ''
  });

  const eventSourceRef = useRef<EventSource | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // 5 minute timeout for source collection
    timeoutRef.current = setTimeout(() => {
      console.warn('Source collection timeout - no activity for 5 minutes');
      setError('Source collection timeout. The process took too long. Please try again.');
      cleanupConnection();
    }, 300000);
  };

  const cleanupConnection = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const startDeepResearch = async () => {
    if (!topic.trim()) {
      setError('Please enter a research topic');
      return;
    }

    setIsStarting(true);
    setError(null);
    setStep('progress');

    // Reset progress state
    setStages({
      query_generation: 'pending',
      source_collection: 'pending',
      url_validation: 'pending',
      content_processing: 'pending',
      indexing: 'pending',
      planning: 'pending',
      writing: 'pending'
    });
    setMessages({
      query_generation: '',
      source_collection: '',
      url_validation: '',
      content_processing: '',
      indexing: '',
      planning: '',
      writing: ''
    });

    try {
      // Prepare research data
      const researchData = {
        topic: topic.trim(),
        description: description.trim() || topic.trim()
      };

      // Start SSE connection for progress updates (collect sources only)
      const queryParams = new URLSearchParams({
        topic: researchData.topic,
        description: researchData.description,
        contentType: contentType,
        contentLength: contentLength,
        sourceAge: sourceAge
      });

      const eventSource = new EventSource(
        `/api/research/collect-sources-only?${queryParams.toString()}`
      );
      eventSourceRef.current = eventSource;

      // Set initial timeout
      resetTimeout();

      eventSource.onmessage = (event) => {
        try {
          const data: ResearchProgressEvent = JSON.parse(event.data);
          resetTimeout();
          handleProgressUpdate(data);
        } catch (err) {
          console.error('Failed to parse SSE message:', err);
        }
      };

      eventSource.onerror = (err) => {
        console.error('SSE error:', err);
        setError('Connection lost. Please try again.');
        setIsStarting(false);
        cleanupConnection();
      };
    } catch (err) {
      console.error('Failed to start deep research:', err);
      setError(err instanceof Error ? err.message : 'Failed to start research');
      setIsStarting(false);
      setStep('input');
    }
  };

  // Helper: Handle stage update event (extracted to reduce complexity)
  const handleStageUpdate = (event: ResearchProgressEvent) => {
    if (!event.stage) return;

    setCurrentStage(event.stage);

    if (event.status) {
      setStages(prev => ({
        ...prev,
        [event.stage!]: event.status!
      }));
    }

    if (event.message) {
      setMessages(prev => ({
        ...prev,
        [event.stage!]: event.message!
      }));
    }
  };

  // Helper: Handle completion event (extracted to reduce complexity)
  const handleComplete = async (event: ResearchProgressEvent) => {
    cleanupConnection();

    if (event.data && event.data.id) {
      try {
        broadcastBriefsChanged();
        // React Query will automatically fetch the brief when the detail page mounts
        // No need to manually load - just navigate
        navigate(`/brief/${event.data.id}`);
      } catch (error) {
        console.error('Failed to navigate to brief:', error);
        setError(error instanceof Error ? error.message : 'Failed to navigate to article brief');
        setIsStarting(false);
      }
    } else {
      setError('Source collection completed but no brief ID returned');
      setIsStarting(false);
    }
  };

  // Helper: Handle error event (extracted to reduce complexity)
  const handleErrorEvent = (event: ResearchProgressEvent) => {
    setError(event.error || 'An error occurred during research');
    setIsStarting(false);
    cleanupConnection();
  };

  const handleProgressUpdate = async (event: ResearchProgressEvent) => {
    switch (event.type) {
      case 'stage_update':
        handleStageUpdate(event);
        break;

      case 'complete':
        await handleComplete(event);
        break;

      case 'error':
        handleErrorEvent(event);
        break;
    }
  };

  const handleBackToInput = () => {
    cleanupConnection();
    setStep('input');
    setIsStarting(false);
    setError(null);
  };

  if (step === 'progress') {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Collecting Sources</h1>
            <p className="text-muted-foreground">Topic: {topic}</p>
          </div>
          {!isStarting && (
            <Button variant="outline" onClick={handleBackToInput}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
        </div>

        <ResearchProgressTracker
          currentStage={currentStage}
          stages={stages}
          messages={messages}
          error={error || undefined}
          sourcesOnly={true}
        />

        {error && (
          <div className="flex justify-center space-x-4">
            <Button onClick={handleBackToInput}>Start Over</Button>
            <Button variant="outline" onClick={onBack}>
              Back to Home
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Deep Research
          </h1>
          <p className="text-muted-foreground mt-1">
            AI-powered automated research and article generation
          </p>
        </div>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      {error && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2 text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">Error:</span>
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>What would you like to research?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="topic">Research Topic *</Label>
            <Input
              id="topic"
              placeholder="e.g., The Future of AI in Healthcare"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={isStarting}
            />
            <p className="text-sm text-muted-foreground">
              Enter a topic you want to research and write about
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Additional Context (optional)</Label>
            <Textarea
              id="description"
              placeholder="Add any specific requirements, focus areas, or context..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isStarting}
              rows={4}
            />
            <p className="text-sm text-muted-foreground">
              Provide additional details to guide the research
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contentType">Content Type</Label>
            <Select
              value={contentType}
              onValueChange={(value) => setContentType(value as ContentTypeValue)}
              disabled={isStarting}
            >
              <SelectTrigger id="contentType" className="text-left">
                <SelectValue placeholder="Select content type" />
              </SelectTrigger>
              <SelectContent align="start">
                {CONTENT_TYPES.map((type) => (
                  <SelectItemWithDescription
                    key={type.value}
                    value={type.value}
                    label={type.label}
                    description={type.description}
                  />
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Choose the type of content you want to create
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contentLength">Target Length</Label>
            <Select
              value={contentLength}
              onValueChange={(value) => setContentLength(value as ContentLengthValue)}
              disabled={isStarting}
            >
              <SelectTrigger id="contentLength" className="text-left">
                <SelectValue placeholder="Select target length" />
              </SelectTrigger>
              <SelectContent align="start">
                {CONTENT_LENGTHS.map((length) => (
                  <SelectItemWithDescription
                    key={length.value}
                    value={length.value}
                    label={length.label}
                    description={length.description}
                  />
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Select the desired article length
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sourceAge">Maximum Source Age</Label>
            <Select
              value={sourceAge}
              onValueChange={(value) => setSourceAge(value as SourceAgeValue)}
              disabled={isStarting}
            >
              <SelectTrigger id="sourceAge" className="text-left">
                <SelectValue placeholder="Select maximum source age" />
              </SelectTrigger>
              <SelectContent align="start">
                {SOURCE_AGE_OPTIONS.map((age) => (
                  <SelectItemWithDescription
                    key={age.value}
                    value={age.value}
                    label={age.label}
                    description={age.description}
                  />
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Filter sources by publication date
            </p>
          </div>

          <div className="bg-muted/50 p-4 rounded-lg space-y-2">
            <h4 className="font-medium text-sm">What happens next?</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• AI generates optimized search queries</li>
              <li>• Discovers and validates relevant sources</li>
              <li>• Pre-fills the article form with collected URLs</li>
              <li>• You can review and edit sources before continuing</li>
            </ul>
            <p className="text-xs text-muted-foreground pt-2">
              This process typically takes 1-3 minutes
            </p>
          </div>

          <Button
            onClick={startDeepResearch}
            disabled={isStarting || !topic.trim()}
            size="lg"
            className="w-full"
          >
            {isStarting ? (
              <>
                <Sparkles className="h-5 w-5 mr-2 animate-pulse" />
                Collecting Sources...
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5 mr-2" />
                Collect Sources
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
